import * as React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import type { WrappedFieldArrayProps } from 'redux-form';

import type { AddressArrayFM } from 'af-root/scenes/Company/WorkOrders/Order/formModel';

import { FormFieldArrayStatus } from '@acceligentllc/shared/enums/formFieldArrayStatus';

import AddressField from 'af-components/SharedForms/WorkLocationAddress/AddressField';

import styles from './styles.module.scss';
import TimezoneHeader from './TimezoneHeader';

interface AddressRemoveOnDeleteProps extends SharedOwnProps {
	removeOnDelete: true;
}

interface AddressUpdateOnDeleteProps extends SharedOwnProps {
	removeOnDelete: false;
	selector: (fieldName: string) => AddressArrayFM;
}

interface SharedOwnProps {
	formName: string;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
	onAddressRemove?: (address: AddressArrayFM) => void;
	disableMap?: boolean;
	canBeEmpty?: boolean;
	disabled?: boolean;
	change: (fieldName: string, value: FormFieldArrayStatus | Partial<AddressArrayFM>) => void;
	jobTimezone: Nullable<string>;
	companyTimezone: Nullable<string>;
}

export type OwnProps = AddressRemoveOnDeleteProps | AddressUpdateOnDeleteProps;

type Props = OwnProps & WrappedFieldArrayProps<AddressArrayFM>;

const Addresses: React.FC<Props> = (props) => {
	const {
		fields,
		meta: { error, submitFailed },
		disabled = false,
		disableMap = true,
		onAddressRemove,
		change,
		removeOnDelete,
		formName,
		jobTimezone,
		companyTimezone,
		latitude,
		longitude,
	} = props;

	// On mount
	React.useEffect(() => {
		// Using no status to display address to user on WO form, but not to interfere with validation
		if (!fields.length) {
			fields.push({} as AddressArrayFM);
		}
	}, [fields, fields.length]);

	// Checks whether there are more then 1 non-removed addresses
	// Firs time it counts more then 1 it returns, ugly but skips unnecessary iteration
	const disableRemove = React.useMemo(() => {
		let nonDeletedFieldLength = 0;
		for (let i = 0; i < fields.length; i++) {
			if (fields.get(i).status !== FormFieldArrayStatus.REMOVED) {
				nonDeletedFieldLength++;
				if (nonDeletedFieldLength > 1) {
					return disabled;
				}
			}
		}
		return nonDeletedFieldLength <= 1 || disabled;
	}, [disabled, fields]);

	const addAddress = React.useCallback(() => {
		fields.push({ status: FormFieldArrayStatus.ADDED } as AddressArrayFM);
	}, [fields]);

	const updateContactMethod = (index: number, fieldName: string) => {
		const field = fields.get(index);
		// Using no status to display address to user on WO form, but not to interfere with validation
		if (!field.status) {
			change(`${fieldName}.status`, FormFieldArrayStatus.ADDED);
		}
		if (field.status === FormFieldArrayStatus.OLD) {
			change(`${fieldName}.status`, FormFieldArrayStatus.EDITED);
		}
	};

	const removeAddress = React.useCallback((index: number) => {
		const removedAddress = fields.get(index);
		if (removeOnDelete) {
			fields.remove(index);
		} else {
			change(`${fields.name}[${index}].status`, FormFieldArrayStatus.REMOVED);
		}
		if (removedAddress.status !== FormFieldArrayStatus.ADDED && onAddressRemove) {
			onAddressRemove(removedAddress);
		}
	}, [change, fields, onAddressRemove, removeOnDelete]);

	const clearAddress = React.useCallback((index?: number) => {
		if (index === undefined) {
			return;
		}

		const field = fields.get(index);

		// 1. If a newly added field, just remove it, no need to update BE
		// 2. Else if an already existing field need to remove it from BE
		// 3. Else do nothing
		if (field.status === FormFieldArrayStatus.ADDED) {
			fields.remove(index);
			fields.push({} as AddressArrayFM);
		} else if (field.status === FormFieldArrayStatus.EDITED
			|| field.status === FormFieldArrayStatus.OLD
		) {
			change(`${fields.name}[${index}].status`, FormFieldArrayStatus.REMOVED);
			fields.push({} as AddressArrayFM);
		}
	}, [change, fields]);

	const renderAddressField = (addressName: string, index: number) => {
		if (removeOnDelete === false) {
			const { selector } = props;
			if (selector(addressName).status === FormFieldArrayStatus.REMOVED) {
				return null;
			}
		}

		const address = fields.get(index);

		return (
			<AddressField
				backupPropName={`${addressName}.address.backup`}
				cityPropName={`${addressName}.address.city`}
				clearAddress={clearAddress}
				country={address?.address?.country}
				countryPropName={`${addressName}.address.country`}
				disabled={disabled}
				disableMap={!!disableMap || !!disabled}
				disableRemove={disableRemove}
				formName={formName}
				index={index}
				key={index}
				latitude={address?.address?.backup?.latitude ?? null}
				locationPropName={`${addressName}.address.location`}
				longitude={address?.address?.backup?.longitude ?? null}
				neighborhoodPropName={`${addressName}.address.neighborhood`}
				notesPropName={`${addressName}.address.notes`}
				onValueUpdate={updateContactMethod.bind(this, index, addressName)}
				postalCodePropName={`${addressName}.address.postalCode`}
				removeAddress={removeAddress}
				routePropName={`${addressName}.address.street`}
				statePropName={`${addressName}.address.state`}
				streetNumberPropName={`${addressName}.address.streetNumber`}
			/>
		);
	};

	const renderTimezoneBanner = React.useCallback(() => {
		return (
			<div className={styles['timezone-container']}>
				<TimezoneHeader
					companyTimezone={companyTimezone}
					jobTimezone={jobTimezone}
					latitude={latitude}
					longitude={longitude}
				/>
			</div>
		);
	}, [companyTimezone, jobTimezone, latitude, longitude]);

	return (
		<>
			{renderTimezoneBanner()}
			<div>
				{fields.map(renderAddressField)}
			</div>
			<Row className={styles.addresses__add}>
				{!disabled &&
					<Col sm={12}>
						<Button className="btn btn--link" onClick={addAddress}>
							<span className="icon-plus" />
							<span>Add Address</span>
						</Button>
						{submitFailed && error && <span>{error}</span>}
					</Col>
				}
			</Row>
		</>
	);
};

export default React.memo(Addresses);
